import {logger} from '../logger';

/** Opens external destinations on the surface preferred by the host shell. */
export interface WindowOpener {
    /**
     * Opens an absolute `http:` or `https:` URL outside the current view.
     *
     * @param url - Absolute URL to hand to the host shell.
     *
     * @example
     * await windowOpener.openUrl('https://www.pexip.com');
     */
    openUrl: (url: string) => Promise<void>;
}

/**
 * Checks that a URL is absolute and uses a web scheme.
 *
 * @param url - Untrusted URL, typically sourced from deployment configuration.
 * @returns Whether the URL is safe to hand to the host shell.
 *
 * @example
 * isWebUrl('javascript:alert(1)'); // false
 */
const isWebUrl = (url: string): boolean => {
    try {
        const {protocol} = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
    } catch {
        return false;
    }
};

/**
 * Creates the browser window opener backed by `window.open`.
 *
 * @returns A dependency that opens URLs in a new browser tab.
 *
 * @example
 * await createBrowserWindowOpener().openUrl('https://www.pexip.com');
 */
const createBrowserWindowOpener = (): WindowOpener => ({
    // `noopener` severs the opener reference, so `window.open` resolves to null
    // and the new tab cannot be inspected or focused from here.
    openUrl: async url => {
        window.open(url, '_blank', 'noopener');
    },
});

let currentWindowOpener: WindowOpener = createBrowserWindowOpener();

/**
 * Replaces the window opener used by the application.
 *
 * @param opener - Host-specific opener, or `undefined` to restore the browser
 * default.
 *
 * @example
 * setWindowOpener(createWindowOpener());
 */
export const setWindowOpener = (opener: WindowOpener | undefined): void => {
    currentWindowOpener = opener ?? createBrowserWindowOpener();
};

/**
 * Opens an external URL through the currently configured window opener.
 *
 * Non-web URLs are rejected so that configuration-provided values cannot
 * execute script or reach the host shell's file handlers.
 *
 * @param url - Absolute `http:` or `https:` URL.
 *
 * @example
 * openExternalUrl('https://www.pexip.com/support');
 */
export const openExternalUrl = (url: string): void => {
    if (!isWebUrl(url)) {
        logger.warn('Refused to open a non-web URL');
        return;
    }
    currentWindowOpener.openUrl(url).catch((error: unknown) => {
        logger.warn({error}, 'Failed to open an external URL');
    });
};
